/* PP PRODUCT CARD — reveal the hover image only once it has actually loaded.
 *
 * THE BUG THIS FIXES. The second image is stacked over the first and faded in on hover. It is
 * loading="lazy", and measured on the Worldwide store 03/08/2026, 14 of 15 cards already on screen
 * still had that image UNLOADED. So the fade ran on an <img> with no pixels in it: the element sat
 * at opacity 1 painting nothing, and the first photograph showed straight through. What a shopper
 * saw was two garments at once — reported by the shop owner as overlapping images.
 *
 * WHY IT LOOKED LIKE AN ASPECT-RATIO PROBLEM AND IS NOT. The first guess was that the hover shot
 * letterboxed inside the frame and let the one below show through the empty bands. Measured and
 * disproved: the cards run object-fit: cover (settings.pp_card_fit defaults to "cover"), and on
 * MERRYWEATHER DRESS the hover shot paints 336x501 into a 336x420 frame — it covers completely,
 * there are no bands. A fix based on that wrong reading (an opaque background on the image) was
 * written and then reverted: while the image is still downloading it would have hidden the first
 * photograph behind a white rectangle, trading a ghosted image for a white flash.
 *
 * THE RULE THIS ENFORCES: never fade in an image that has nothing to paint. CSS cannot ask whether
 * an image has loaded, so that state has to be put into the DOM from here.
 *
 * WHY NOT JUST loading="eager". That would fetch a second full-size photograph for every card on
 * the page — 24 cards on a collection page means 48 images — on a shop where over 90% of traffic
 * is mobile. The hover image is worth nothing until there is a mouse, so it is fetched on the first
 * sign of one and not before.
 */
(() => {
  const READY = 'data-pp-hover-ready';

  const markReady = (img) => img.setAttribute(READY, 'true');

  /* An image can finish loading before this script runs, and `load` does not fire retroactively —
   * so check complete/naturalWidth first. naturalWidth matters as well as complete: a FAILED image
   * also reports complete, and revealing a broken one would put us back to showing nothing. */
  const track = (img) => {
    if (img.hasAttribute(READY)) return;
    if (img.complete && img.naturalWidth > 0) {
      markReady(img);
      return;
    }
    /* Deliberately NOT de-duplicated. A WeakSet guard was tried here to avoid stacking a second
     * {once:true} listener when the same image is tracked at startup and again on first hover, and
     * it was removed: this theme reuses <img> NODES across re-renders. morph.js keeps the existing
     * node and only patches its attributes (assets/morph.js copyAttributes), which strips
     * data-pp-hover-ready — the server never emits it — and resets loading to lazy, while the
     * MutationObserver below watches childList only and so never sees an attribute-only patch. A
     * WeakSet keyed on node identity therefore remembers a node forever and blocks re-tracking it
     * after a collection filter swaps a NEW photograph into that same element: the image loads and
     * is never marked ready, so the hover swap silently stops working on that card.
     *
     * I could not reproduce that in the browser across three attempts — the synchronous
     * complete/naturalWidth check above catches it whenever the replacement image is already
     * cached, which it usually is — but the gap is plain in the code, and what the guard bought was
     * only a few duplicate {once:true} listeners that fire, set the same attribute to the same
     * value, and remove themselves. That is not worth a correctness risk on the sales grid. */
    img.addEventListener('load', () => markReady(img), { once: true });
  };

  /* Pull the fetch forward on the first hint of a pointer. pointerenter fires once per card on the
   * way in and does not bubble-spam like pointermove, so the whole grid costs one listener and each
   * card is upgraded at most once. Cards that are never hovered are never fetched. */
  const warm = (card) => {
    const deferred = card.querySelector('template[data-pp-hover-image]');
    if (deferred) deferred.replaceWith(deferred.content.cloneNode(true));
    const img = card.querySelector('.pp-pc__img--hover');
    if (!img || img.hasAttribute(READY)) return;
    // The inert template keeps this image out of the initial request queue until it is needed.
    if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
    track(img);
  };

  /* MOUSE ONLY on the pointer path. pointerenter also fires for a FINGER, so without this check a
   * tap would fetch a second full-size photograph that the CSS can never reveal — the hover rule in
   * pp-product-card.css lives inside @media (hover: hover) and (pointer: fine). Over 90% of this
   * shop's traffic is mobile, so that would have meant paying for a wasted image on almost every
   * product a shopper touches. Read pointerType rather than matchMedia so a hybrid laptop still
   * warms up when a real mouse is used and stays quiet when the same screen is tapped. */
  const onPointer = (event) => {
    /* Strict equality, no truthy-gate. `event.pointerType && event.pointerType !== 'mouse'` would
     * let a pointerType of "" through, which is what a hand-built `new PointerEvent(...)` defaults
     * to — and this theme does dispatch one of those (header-menu.js). That one targets a menu item
     * and so cannot reach a card today, but the point of this guard is protecting mobile data, and
     * a guard that opens on an unknown pointer type is the wrong way round. */
    if (event.pointerType !== 'mouse') return;
    const card = event.target.closest?.('.pp-pc');
    if (card) warm(card);
  };

  /* Focus is NOT gated the same way: the keyboard rule in pp-product-card.css sits deliberately
   * outside that media query, because a touchscreen laptop reports pointer:coarse and a Tab user
   * there would otherwise never see the second image at all. */
  const onFocus = (event) => {
    if (!event.target.matches?.(':focus-visible')) return;
    const card = event.target.closest?.('.pp-pc');
    if (card) warm(card);
  };

  /* querySelectorAll searches DESCENDANTS only, so a node that is itself the hover image would be
   * skipped. Nothing in this theme inserts a bare <img> today — every path adds a whole card or
   * grid cell — but the check costs one matches() call and removes a way for this to fail silently
   * if some future markup does. */
  const scan = (root) => {
    if (root.matches?.('.pp-pc__img--hover')) track(root);
    root.querySelectorAll?.('.pp-pc__img--hover').forEach(track);
  };

  scan(document);

  /* Keyboard users reach the card by focus, never by pointer, so they need the same warm-up or the
   * image would never start loading for them at all. focusin bubbles; focus does not. */
  document.addEventListener('focusin', onFocus);
  document.addEventListener('pointerenter', onPointer, { capture: true });

  /* Cards arrive after load in several places here: the Section Rendering API swaps a whole
   * collection grid when a filter changes, and quick-add/recommendations inject markup. A fresh
   * node carries no flag, which is correct — it does need tracking again. */
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1) scan(node);
      }
    }
  });
  /* document.body, not document.documentElement. Shopify and its apps inject script and link tags
   * into <head> continuously, and a card can never render there — watching documentElement meant
   * waking this callback for every one of those. Still deliberately NOT scoped tighter than body:
   * cards arrive in several places (collection grid, search results, the homepage rows, the product
   * page recommendation strip, quick-add) and naming those containers would mean a card appearing
   * somewhere unlisted silently loses its hover image — a failure nobody would notice for months.
   * The residual cost is one querySelectorAll per added subtree during unrelated churn such as
   * cart-drawer updates or predictive search typing; accepted knowingly rather than traded for a
   * silent-miss risk. Revisit only with a real profile on a mid-range Android, not on suspicion. */
  observer.observe(document.body, { childList: true, subtree: true });
})();
